import { FormEvent, useEffect, useId, useState } from "react";
import { useLanguage } from "../../LanguageContext";
import { useUi } from "../../UiContext";

const FORMSUBMIT_ID = "ac5bf5846472ba659fba037dbb8d745b";
const DESKTOP_MQ = "(min-width: 768px)";

type Status = "idle" | "sending" | "success" | "error";

export default function ContactForm() {
	const { text } = useLanguage();
	const { showNotice } = useUi();
	const panelId = useId();
	const [status, setStatus] = useState<Status>("idle");
	const [open, setOpen] = useState(() =>
		typeof window !== "undefined"
			? window.matchMedia(DESKTOP_MQ).matches
			: false,
	);
	const [isDesktop, setIsDesktop] = useState(() =>
		typeof window !== "undefined"
			? window.matchMedia(DESKTOP_MQ).matches
			: false,
	);

	useEffect(() => {
		const mq = window.matchMedia(DESKTOP_MQ);
		const sync = () => {
			const desktop = mq.matches;
			setIsDesktop(desktop);
			setOpen(desktop);
		};
		sync();
		mq.addEventListener("change", sync);
		return () => mq.removeEventListener("change", sync);
	}, []);

	async function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const form = event.currentTarget;
		const data = new FormData(form);
		const name = String(data.get("name") || "").trim();
		const email = String(data.get("email") || "").trim();
		const message = String(data.get("message") || "").trim();
		if (!name || !email || !message) return;

		setStatus("sending");
		try {
			const response = await fetch(
				`https://formsubmit.co/ajax/${FORMSUBMIT_ID}`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
					},
					body: JSON.stringify({
						name,
						email,
						message,
						_subject: `Portfolio contact — ${name}`,
						_template: "table",
					}),
				},
			);
			if (!response.ok) throw new Error("send failed");
			form.reset();
			setStatus("success");
			showNotice(text.footer.contact.success);
		} catch {
			setStatus("error");
			showNotice(text.footer.contact.error);
		}
	}

	const expanded = isDesktop || open;

	return (
		<form
			className={`footer-contact${expanded ? " is-open" : ""}`}
			onSubmit={onSubmit}
			noValidate
		>
			{isDesktop ? (
				<h3 className="footer-contact-title">{text.footer.contact.label}</h3>
			) : (
				<button
					type="button"
					className="footer-contact-trigger"
					aria-expanded={expanded}
					aria-controls={panelId}
					onClick={() => setOpen((value) => !value)}
				>
					<span>{text.footer.contact.label}</span>
					<svg
						className="footer-contact-chevron"
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<path d="m6 9 6 6 6-6" />
					</svg>
				</button>
			)}
			<div
				id={panelId}
				className="footer-contact-panel"
				role={isDesktop ? undefined : "region"}
				aria-hidden={!expanded}
			>
				<div className="footer-contact-panel-inner">
					<div className="footer-contact-row">
						<label className="footer-contact-field">
							<span>{text.footer.contact.name}</span>
							<input
								type="text"
								name="name"
								autoComplete="name"
								required
								tabIndex={expanded ? undefined : -1}
								disabled={status === "sending"}
							/>
						</label>
						<label className="footer-contact-field">
							<span>{text.footer.contact.email}</span>
							<input
								type="email"
								name="email"
								autoComplete="email"
								required
								tabIndex={expanded ? undefined : -1}
								disabled={status === "sending"}
							/>
						</label>
					</div>
					<label className="footer-contact-field">
						<span>{text.footer.contact.message}</span>
						<textarea
							name="message"
							rows={4}
							required
							tabIndex={expanded ? undefined : -1}
							disabled={status === "sending"}
						/>
					</label>
					<button
						type="submit"
						className="footer-contact-submit"
						tabIndex={expanded ? undefined : -1}
						disabled={status === "sending"}
					>
						{status === "sending"
							? text.footer.contact.sending
							: text.footer.contact.send}
					</button>
				</div>
			</div>
		</form>
	);
}
